/**
 * Tanjong Life · "commission a Completeness Sentinel" — and the honest answer, which is that it is
 * already running.
 *
 * THIS BEAT EXISTS BECAUSE THE ALTERNATIVE WRITES A WRONG RECORD. The surface router reads a
 * making verb plus the word `agent` and hands the sentence to the generic agent builder,
 * which consults nothing scripted, invents a name and persists it into this org. Four of the
 * six autonomous agents here were commissioned before the demo starts, so the phrasing that
 * commissions the other two has to land somewhere truthful rather than somewhere plausible.
 *
 * It creates nothing, and that is deliberate rather than a shortcut. Pointing the commission
 * conversation at a seeded pk would REPLACE the agent row and its whole detail bundle —
 * `demo-born.ts` upserts by pk — and with no boot asset the page would never play its stream
 * again. A fully authored agent would be overwritten to narrate a birth its own v1 release
 * row already dates to 24 August, with a named proposer and approver on it.
 *
 * So it says so, names what it watches and the one thing it refuses to claim, and opens it.
 */
import type { Scenario } from '../director.js';
import { MAKE_COMPLETENESS_SENTINEL } from './tanjong-make-intents.js';

export const tanjongStandingCompleteness: Scenario = {
  id: 'tanjong-standing-completeness',
  orgId: 'tanjong-life',
  /*
   * NO KEYWORDS. `settlement assurance sentinel` as a keyword stole "Build a process for
   * claims payment authorisation with the Settlement Assurance Sentinel driving it" from the
   * build it plainly asks for — these scenarios register before the builds, so a keyword here
   * wins any sentence that merely NAMES the agent. Meaning is the only safe route in.
   */
  keywords: [],
  intents: [MAKE_COMPLETENESS_SENTINEL],
  surface: 'chat',
  steps: [
    {
      do: 'say',
      text: 'The Completeness Sentinel already exists — it was commissioned on 24 August by Bruno Castellanos, approved by Tobias Vance, and it has been running ever since. I am not going to stand a second one up beside it: two agents watching the same queue is the thing this estate is built to prevent, and the second one would have no way of knowing what the first had already opened.',
      sources: [
        'Completeness Sentinel · v1 live since 24 August',
        'Proposed by Bruno Castellanos · approved by Tobias Vance',
      ],
      delay: 900,
    },
    {
      do: 'insight',
      title: 'What it already watches',
      body: 'Workflow 01 · claims document completeness ends when a person has what they need. This agent watches the window between a file being judged complete and an assessor opening it — a window no activity in workflow 01 owns. It runs 4 probes, 1 held, opens a case at a named person with the evidence attached, and holds all five of its authorities at zero of zero with Halt as the action on breach — it reports, and it decides nothing.',
      sources: ['Process tl-pr11', 'Authorities · 5, every one 0 of 0 · Halt on breach'],
      delay: 1300,
    },
    {
      do: 'say',
      text: 'And the thing worth hearing before you take my word for any of it: Telling an unanswered request from an unread reply, at 36 out of 100. It can see a request went out on the third and no document on the file on the fourteenth; it cannot see which of those happened, because the reply channel is not one it reads. Its overall evaluation is 84.',
      delay: 1300,
    },
    {
      do: 'link',
      label: 'Open Completeness Sentinel',
      href: '/agents/completeness-sentinel',
      delay: 600,
    },
  ],
};
